import { CanceledError, type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import type { ErrorInterceptorFn, GetErrorMessageFn } from '../types';

type On403Option = 'silent' | ((error: AxiosError) => void | Promise<void>);

export interface HttpErrorInterceptorOptions {
  /** 401 处理，典型用法：跳转登录 */
  on401?: (error: AxiosError) => void | Promise<void>;
  /** 401 后是否 reject，默认 true。设为 false 则 resolve error（兼容不抛出的旧行为） */
  reject401?: boolean;
  /** 403 处理，'silent' 表示静默拒绝（默认） */
  on403?: On403Option;
  /** 403 后是否 reject，默认 true。设为 false 则 resolve error */
  reject403?: boolean;
  /** 是否静默处理 CanceledError，默认 true */
  silentOnCancel?: boolean;
  /** 从错误数据中提取错误信息，返回 undefined 则回退到 defaultErrorMessage */
  getErrorMessage?: GetErrorMessageFn;
  /** 展示错误信息的回调 */
  showError?: (msg: string) => void;
  /** 兜底错误信息，默认 'unknown error' */
  defaultErrorMessage?: string;
  /** 是否受 config.autoErrorHandle 控制，默认 true */
  respectAutoErrorHandle?: boolean;
  /** 为 true 时 SSR 端跳过错误提示，默认 false */
  clientOnly?: boolean;
}

type AutoHandleConfig = InternalAxiosRequestConfig & { autoErrorHandle?: boolean };

/**
 * 处理 HTTP 层错误（4xx / 5xx）。
 * 处理顺序：CanceledError → 403 → 401 → clientOnly 检查 → autoErrorHandle 检查 → showError
 */
export const createHttpErrorInterceptor = (
  options: HttpErrorInterceptorOptions = {},
): ErrorInterceptorFn => {
  const {
    on401,
    reject401 = true,
    on403 = 'silent',
    reject403 = true,
    silentOnCancel = true,
    getErrorMessage,
    showError,
    defaultErrorMessage = 'unknown error',
    respectAutoErrorHandle = true,
    clientOnly = false,
  } = options;

  return async (error: AxiosError) => {
    // CanceledError
    if (silentOnCancel && error instanceof CanceledError) {
      return Promise.reject(error);
    }

    const status = error.response?.status ?? error.status;

    // 403
    if (status === 403) {
      if (on403 !== 'silent') {
        await on403(error);
      }
      return reject403 ? Promise.reject(error) : error;
    }

    // 401
    if (status === 401) {
      if (on401) {
        await on401(error);
      }
      return reject401 ? Promise.reject(error) : error;
    }

    // SSR 端跳过错误提示
    if (clientOnly && import.meta.server) {
      return Promise.reject(error);
    }

    // autoErrorHandle 检查
    const config = error.config as AutoHandleConfig | undefined;
    if (respectAutoErrorHandle && config?.autoErrorHandle === false) {
      return Promise.reject(error);
    }

    // 展示错误信息
    if (showError) {
      const msg =
        getErrorMessage?.(error.response?.data) ??
        ((error.response?.data as Record<string, unknown>)?.msg as string | undefined) ??
        defaultErrorMessage;
      showError(msg);
    }

    return Promise.reject(error);
  };
};
